import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AnimatePresence, motion } from 'framer-motion';
import { EColors, EColors2, EFonts, EVariables } from 'libs/styles/styling';
import { useAnalytics } from 'libs/utils/site/analytics';
import React from 'react';
import styled, { css } from 'styled-components/macro';

interface Props {
    topics: {
        q: string;
        a: React.ReactNode;
    }[];
    isWide: boolean;
    className?: string;
}

export const FAQ = (props: Props): JSX.Element => {
    const analytics = useAnalytics();
    const [selectedIndex, setSelectedIndex] = React.useState<number>(-1);

    const { isWide } = props;

    function toggle(index: number, topic: { q: string; a: React.ReactNode }) {
        if (index === selectedIndex) {
            setSelectedIndex(-1);
        } else {
            setSelectedIndex(index);
            analytics.track('event_ui.detail.open_clicked', {
                type: 'information',
                storyId: '.',
                title: topic.q
            });
        }
    }

    return (
        <S_FAQ className={props.className} $isWide={isWide}>
            {props.topics.map((topic, index) => {
                return (
                    <S_Topic
                        key={topic.q}
                        $isWide={isWide}
                        $selected={selectedIndex === index}
                        onClick={() => {
                            if (selectedIndex !== index) {
                                toggle(index, topic);
                            }
                        }}
                    >
                        {isWide && (
                            <>
                                <S_FontIcon
                                    icon={['far', selectedIndex === index ? 'minus' : 'plus']}
                                    onClick={() => toggle(index, topic)}
                                />
                                <S_Right>
                                    <S_Title onClick={() => toggle(index, topic)} $isWide={isWide}>
                                        {topic.q}
                                    </S_Title>

                                    <AnimatePresence>
                                        {selectedIndex === index && (
                                            <S_Answer
                                                key={topic.q}
                                                initial={{ height: 0 }}
                                                animate={{ height: 'unset' }}
                                                transition={{ duration: 0.2, ease: 'easeInOut' }}
                                                exit={{ height: 0 }}
                                                $isWide={isWide}
                                            >
                                                {topic.a}
                                            </S_Answer>
                                        )}
                                    </AnimatePresence>
                                </S_Right>
                            </>
                        )}
                        {!isWide && (
                            <>
                                <S_Top>
                                    <S_FontIcon
                                        icon={['far', selectedIndex === index ? 'minus' : 'plus']}
                                        onClick={() => toggle(index, topic)}
                                    />

                                    <S_Title onClick={() => toggle(index, topic)} $isWide={isWide}>
                                        {topic.q}
                                    </S_Title>
                                </S_Top>
                                <AnimatePresence>
                                    {selectedIndex === index && (
                                        <S_Bot>
                                            <S_Answer
                                                key={topic.q}
                                                initial={{ height: 0 }}
                                                animate={{ height: 'unset' }}
                                                transition={{ duration: 0.2, ease: 'easeInOut' }}
                                                exit={{ height: 0 }}
                                                $isWide={isWide}
                                            >
                                                {topic.a}
                                            </S_Answer>
                                        </S_Bot>
                                    )}
                                </AnimatePresence>
                            </>
                        )}
                    </S_Topic>
                );
            })}
        </S_FAQ>
    );
};

const S_Topic = styled.div<{ $selected: boolean; $isWide: boolean }>`
    display: flex;
    gap: 13px;
    padding: 15px 15px;
    justify-content: flex-start;
    align-items: flex-start;

    user-select: none;

    background-color: #fafcfe;
    border-radius: 15px;
    flex-direction: column;
    ${(props) => {
        if (!props.$selected) {
            return css`
                cursor: pointer;
            `;
        }
    }}
    ${(props) => {
        if (props.$isWide) {
            return css`
                flex-direction: row;
                padding: 20px 25px;
                gap: 18px;
                gap: 36px;
                padding: 33px 40px;
                border-bottom: 1px solid #eaf0f5;
                border-radius: unset;
                background-color: unset;
                &:last-child {
                    border-bottom: none;
                }
            `;
        }
    }}

    ${(props) => {
        if (props.$isWide && !props.$selected) {
            return css`
                &:hover {
                    background-color: #eaf0f5;
                }
            `;
        }
    }}
`;

const S_FAQ = styled.div<{ $isWide: boolean }>`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 15px;
    ${(props) => {
        if (props.$isWide) {
            return css`
                background-color: ${EColors.WHITE};
                border-radius: ${EVariables.Radius2};
                gap: unset;
                width: 800px;
                align-self: center;
                ${S_Topic}:nth-child(1) {
                    border-top-left-radius: ${EVariables.Radius2};
                    border-top-right-radius: ${EVariables.Radius2};
                }
                ${S_Topic}:last-child {
                    border-bottom-left-radius: ${EVariables.Radius2};
                    border-bottom-right-radius: ${EVariables.Radius2};
                }
            `;
        }
    }}
`;

const S_Answer = styled(motion.div)<{ $isWide: boolean }>`
    color: ${EColors2.DARK};
    font-family: ${EFonts.Regular};
    font-size: 15px;
    line-height: 28px;
    margin: 0px 0 0 0;
    overflow: hidden;
    ${(props) => {
        if (props.$isWide) {
            return css`
                font-size: 16px;
                line-height: 31px;
                margin: 10px 0 0 0;
            `;
        }
    }}
`;

const S_FontIcon = styled(FontAwesomeIcon)`
    flex-shrink: 0;
    margin: 2px 0 0 0;
    font-size: 20px;
    color: ${EColors2.DARK};
    cursor: pointer;
`;

const S_Right = styled.div`
    width: 100%;
`;

const S_Title = styled.div<{ $isWide: boolean }>`
    color: ${EColors2.DARK};
    font-family: ${EFonts.Medium};
    font-size: 15px;
    letter-spacing: -0.3px;
    line-height: 22px;
    margin: 2px 0px 0 12px;
    cursor: pointer;
    ${(props) => {
        if (props.$isWide) {
            return css`
                margin: 2px 0 0 0;
                font-size: 18px;
                letter-spacing: -0.54px;
            `;
        }
    }}
`;

const S_Top = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
`;

const S_Bot = styled.div`
    width: 100%;
    margin: -10px 0 0 0;
    padding: 0 5px;
`;
